<template>
  <div class="landing-page">
    <!-- Navigation -->
    <nav class="navbar">
      <div class="nav-container">
        <div class="nav-logo">
          <AppLogo class="logo-img" />
        </div>
        <div class="nav-links">
          <a href="#features">Features</a>
          <a href="#how-it-works">How It Works</a>
          <a href="#pricing">Pricing</a>
        </div>
        <div class="nav-buttons">
          <router-link to="/login" class="btn-login">Login</router-link>
          <router-link to="/registration" class="btn-signup">Sign Up</router-link>
        </div>
      </div>
    </nav>

    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-glow"></div>
      <div class="hero-content">
        <div class="hero-icons">
          <div class="icon-circle">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
            </svg>
          </div>
          <div class="icon-line"></div>
          <div class="icon-circle icon-main">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
              <path d="M2 17l10 5 10-5"></path>
              <path d="M2 12l10 5 10-5"></path>
            </svg>
          </div>
          <div class="icon-line"></div>
          <div class="icon-circle">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
        </div>
        
        <h1>The simple way to<br><span class="gradient-text">tailor your resume</span></h1>
        <p class="hero-subtitle">
          AI-powered resume customization that matches your skills to any job posting.<br>
          Works on LinkedIn, Indeed, and Glassdoor.
        </p>
        
        <div class="hero-cta">
          <router-link to="/registration" class="btn-primary">Get Started Free</router-link>
          <p class="cta-note">3 free resumes • No credit card required</p>
        </div>
      </div>
      
      <div class="hero-grid"></div>
    </section>

    <!-- Features Section -->
    <section id="features" class="features">
      <h2>Why job seekers love ResyMaid</h2>
      <div class="features-grid">
        <div class="feature-card">
          <div class="feature-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
          </div>
          <h3>Save Hours</h3>
          <p>Generate tailored resumes in seconds, not hours. Focus on what matters — landing the interview.</p>
        </div>
        <div class="feature-card">
          <div class="feature-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
            </svg>
          </div>
          <h3>ATS Optimized</h3>
          <p>Beat applicant tracking systems with keyword-optimized resumes that get you past the bots.</p>
        </div>
        <div class="feature-card">
          <div class="feature-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="3" y1="9" x2="21" y2="9"></line>
              <line x1="9" y1="21" x2="9" y2="9"></line>
            </svg>
          </div>
          <h3>One-Click Magic</h3>
          <p>Browse any job posting and generate a custom resume with a single click. It's that easy.</p>
        </div>
      </div>
    </section>

    <!-- How It Works Section -->
    <section id="how-it-works" class="how-it-works">
      <h2>How it works</h2>
      <div class="steps">
        <div class="step">
          <div class="step-number">1</div>
          <h3>Upload your resume</h3>
          <p>Upload your base resume once. We'll use it as the foundation for all your tailored versions.</p>
        </div>
        <div class="step-arrow">→</div>
        <div class="step">
          <div class="step-number">2</div>
          <h3>Find a job</h3>
          <p>Browse LinkedIn, Indeed, or Glassdoor. When you find a job you like, click our extension.</p>
        </div>
        <div class="step-arrow">→</div>
        <div class="step">
          <div class="step-number">3</div>
          <h3>Generate & apply</h3>
          <p>Get a perfectly tailored resume in seconds. Download as PDF, DOCX, or HTML and apply.</p>
        </div>
      </div>
    </section>

    <!-- Pricing Section -->
    <section id="pricing" class="pricing">
      <h2>Simple, fair pricing</h2>
      <p class="pricing-subtitle">Pay only for what you use. No subscriptions, no hidden fees.</p>
      
      <div class="pricing-cards">
        <div class="pricing-card">
          <h3>Starter</h3>
          <div class="price">$2.99</div>
          <p class="credits">15 credits</p>
          <ul>
            <li>✓ 15 tailored resumes</li>
            <li>✓ PDF, DOCX, HTML export</li>
            <li>✓ ATS optimization</li>
            <li>✓ Never expires</li>
          </ul>
          <router-link to="/registration" class="btn-outline">Get Started</router-link>
        </div>
        
        <div class="pricing-card popular">
          <div class="popular-badge">Most Popular</div>
          <h3>Standard</h3>
          <div class="price">$6.99</div>
          <p class="credits">50 credits</p>
          <ul>
            <li>✓ 50 tailored resumes</li>
            <li>✓ PDF, DOCX, HTML export</li>
            <li>✓ ATS optimization</li>
            <li>✓ Never expires</li>
          </ul>
          <router-link to="/registration" class="btn-primary">Get Started</router-link>
        </div>
        
        <div class="pricing-card">
          <h3>Pro</h3>
          <div class="price">$14.99</div>
          <p class="credits">150 credits</p>
          <ul>
            <li>✓ 150 tailored resumes</li>
            <li>✓ PDF, DOCX, HTML export</li>
            <li>✓ ATS optimization</li>
            <li>✓ Never expires</li>
          </ul>
          <router-link to="/registration" class="btn-outline">Get Started</router-link>
        </div>
      </div>
    </section>

    <!-- Referral Section -->
    <section class="referral-section">
      <div class="referral-content">
        <div class="referral-badge">
          <span>Earn Free Credits</span>
        </div>
        <h2>Refer friends, get <span class="gradient-text">10 free resumes</span></h2>
        <p class="referral-description">
          Share ResyMaid with friends and earn 10 free resume credits for each friend who signs up and makes their first purchase. It's a win-win!
        </p>
        <div class="referral-steps-inline">
          <div class="referral-step">
            <div class="step-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                <polyline points="16 6 12 2 8 6"></polyline>
                <line x1="12" y1="2" x2="12" y2="15"></line>
              </svg>
            </div>
            <span>Share your link</span>
          </div>
          <div class="step-connector">→</div>
          <div class="referral-step">
            <div class="step-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="8.5" cy="7" r="4"></circle>
                <line x1="20" y1="8" x2="20" y2="14"></line>
                <line x1="23" y1="11" x2="17" y2="11"></line>
              </svg>
            </div>
            <span>Friend signs up & buys</span>
          </div>
          <div class="step-connector">→</div>
          <div class="referral-step">
            <div class="step-icon highlight">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                <line x1="7" y1="7" x2="7.01" y2="7"></line>
              </svg>
            </div>
            <span>Get 10 free credits!</span>
          </div>
        </div>
        <router-link to="/registration" class="btn-referral">
          Sign Up & Start Referring
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="5" y1="12" x2="19" y2="12"></line>
            <polyline points="12 5 19 12 12 19"></polyline>
          </svg>
        </router-link>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <h2>Ready to land your dream job?</h2>
      <p>Join thousands of job seekers who've upgraded their job search with ResyMaid.</p>
      <router-link to="/registration" class="btn-primary btn-large">Start Free Today</router-link>
    </section>

    <!-- Footer -->
    <footer class="footer">
      <div class="footer-content">
        <div class="footer-brand">
          <div class="footer-brand-logo">
            <AppLogo class="footer-logo" />
          </div>
          <p>AI-powered resume tailoring for modern job seekers.</p>
        </div>
        <div class="footer-links">
          <div class="footer-column">
            <h4>Product</h4>
            <a href="#features">Features</a>
            <a href="#pricing">Pricing</a>
            <a href="#how-it-works">How It Works</a>
          </div>
          <div class="footer-column">
            <h4>Legal</h4>
            <router-link to="/privacy">Privacy Policy</router-link>
            <router-link to="/terms">Terms of Service</router-link>
          </div>
        </div>
      </div>
      <div class="footer-bottom">
        <p>&copy; {{ new Date().getFullYear() }} ResyMaid. All rights reserved.</p>
      </div>
    </footer>
  </div>
</template>

<script>
import AppLogo from '@/components/AppLogo.vue';

export default {
  name: 'LandingPage',
  components: { AppLogo },
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.landing-page {
  min-height: 100vh;
  background: #0a0a0f;
  color: #ffffff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Navigation */
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  padding: 1rem 2rem;
  background: rgba(10, 10, 15, 0.8);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.nav-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 1;
  min-width: 0;
}

.logo-img {
  height: 64px;
  max-width: 220px;
  width: auto;
  object-fit: contain;
}

.nav-links {
  display: flex;
  gap: 2rem;
}

.nav-links a {
  color: #a1a1aa;
  text-decoration: none;
  font-size: 14px;
  transition: color 0.2s;
}

.nav-links a:hover {
  color: #ffffff;
}

.nav-buttons {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-shrink: 0;
}

.btn-login {
  color: #ffffff;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: rgba(255, 255, 255, 0.08);
  white-space: nowrap;
  transition: all 0.2s;
}

.btn-login:hover {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.14);
  border-color: rgba(255, 255, 255, 0.35);
}

.btn-signup {
  background: #ffffff;
  color: #0a0a0f;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 8px;
  white-space: nowrap;
  transition: all 0.2s;
}

.btn-signup:hover {
  background: #e4e4e7;
}

/* Hero Section */
.hero {
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 120px 2rem 80px;
  overflow: hidden;
}

.hero-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 800px;
  height: 400px;
  background: radial-gradient(ellipse, rgba(139, 92, 246, 0.3) 0%, transparent 70%);
  filter: blur(60px);
  pointer-events: none;
}

.hero-content {
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 800px;
}

.hero-icons {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  margin-bottom: 3rem;
}

.icon-circle {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(30, 30, 40, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a1a1aa;
}

.icon-main {
  width: 72px;
  height: 72px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border: none;
  color: #ffffff;
}

.icon-line {
  width: 60px;
  height: 1px;
  background: rgba(255, 255, 255, 0.2);
}

h1 {
  font-size: 56px;
  font-weight: 700;
  line-height: 1.1;
  margin-bottom: 1.5rem;
}

.gradient-text {
  background: linear-gradient(135deg, #8b5cf6 0%, #c084fc 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: 18px;
  color: #a1a1aa;
  line-height: 1.6;
  margin-bottom: 2.5rem;
}

.hero-cta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.btn-primary {
  background: #ffffff;
  color: #0a0a0f;
  text-decoration: none;
  font-size: 16px;
  font-weight: 600;
  padding: 14px 32px;
  border-radius: 12px;
  transition: all 0.2s;
  border: none;
  cursor: pointer;
}

.btn-primary:hover {
  background: #e4e4e7;
  transform: translateY(-2px);
}

.cta-note {
  font-size: 14px;
  color: #71717a;
}

.hero-grid {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 200px;
  background: linear-gradient(180deg, transparent 0%, rgba(139, 92, 246, 0.1) 100%);
  background-image: 
    linear-gradient(rgba(139, 92, 246, 0.1) 1px, transparent 1px),
    linear-gradient(90deg, rgba(139, 92, 246, 0.1) 1px, transparent 1px);
  background-size: 40px 40px;
  mask-image: linear-gradient(180deg, transparent 0%, black 100%);
  -webkit-mask-image: linear-gradient(180deg, transparent 0%, black 100%);
}

/* Features Section */
.features {
  padding: 100px 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.features h2, .how-it-works h2, .pricing h2, .cta-section h2 {
  font-size: 40px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 3rem;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}

.feature-card {
  background: rgba(30, 30, 40, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;
  transition: all 0.3s;
}

.feature-card:hover {
  border-color: rgba(139, 92, 246, 0.5);
  transform: translateY(-4px);
}

.feature-icon {
  width: 64px;
  height: 64px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8b5cf6;
  margin-bottom: 1.5rem;
}

.feature-card h3 {
  font-size: 20px;
  margin-bottom: 0.75rem;
}

.feature-card p {
  color: #a1a1aa;
  line-height: 1.6;
}

/* How It Works */
.how-it-works {
  padding: 100px 2rem;
  background: rgba(20, 20, 30, 0.5);
}

.steps {
  max-width: 1000px;
  margin: 0 auto;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 1rem;
}

.step {
  flex: 1;
  text-align: center;
  padding: 2rem;
}

.step-number {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: #ffffff;
  font-size: 20px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
}

.step h3 {
  font-size: 18px;
  margin-bottom: 0.75rem;
}

.step p {
  color: #a1a1aa;
  font-size: 14px;
  line-height: 1.6;
}

.step-arrow {
  color: #71717a;
  font-size: 24px;
  margin-top: 3rem;
}

/* Pricing */
.pricing {
  padding: 100px 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.pricing-subtitle {
  text-align: center;
  color: #a1a1aa;
  margin-top: -2rem;
  margin-bottom: 3rem;
}

.pricing-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}

.pricing-card {
  background: rgba(30, 30, 40, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;
  text-align: center;
  position: relative;
}

.pricing-card.popular {
  border-color: #8b5cf6;
  background: rgba(139, 92, 246, 0.1);
}

.popular-badge {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 16px;
  border-radius: 20px;
}

.pricing-card h3 {
  font-size: 20px;
  margin-bottom: 0.5rem;
}

.price {
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.credits {
  color: #a1a1aa;
  margin-bottom: 1.5rem;
}

.pricing-card ul {
  list-style: none;
  padding: 0;
  margin: 0 0 2rem;
  text-align: left;
}

.pricing-card li {
  padding: 0.5rem 0;
  color: #a1a1aa;
  font-size: 14px;
}

.btn-outline {
  display: block;
  background: transparent;
  color: #ffffff;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.2s;
}

.btn-outline:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.4);
}

/* Referral Section */
.referral-section {
  padding: 100px 2rem;
  background: rgba(20, 20, 30, 0.8);
}

.referral-content {
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
}

.referral-badge {
  display: inline-block;
  margin-bottom: 1.5rem;
}

.referral-badge span {
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(239, 68, 68, 0.15));
  border: 1px solid rgba(245, 158, 11, 0.3);
  color: #fbbf24;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 20px;
}

.referral-section h2 {
  font-size: 40px;
  font-weight: 700;
  margin-bottom: 1.5rem;
}

.referral-description {
  font-size: 18px;
  color: #a1a1aa;
  line-height: 1.6;
  margin-bottom: 3rem;
}

.referral-steps-inline {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  margin-bottom: 3rem;
}

.referral-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.step-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a1a1aa;
  transition: all 0.3s;
}

.step-icon.highlight {
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  border: none;
  color: #ffffff;
}

.referral-step span {
  color: #a1a1aa;
  font-size: 14px;
  font-weight: 500;
}

.step-connector {
  color: #52525b;
  font-size: 20px;
  margin-bottom: 30px;
}

.btn-referral {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #f59e0b, #ef4444);
  color: #ffffff;
  text-decoration: none;
  font-size: 16px;
  font-weight: 600;
  padding: 16px 32px;
  border-radius: 12px;
  transition: all 0.2s;
}

.btn-referral:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(245, 158, 11, 0.4);
}

/* CTA Section */
.cta-section {
  padding: 100px 2rem;
  text-align: center;
  background: linear-gradient(180deg, transparent 0%, rgba(139, 92, 246, 0.1) 100%);
}

.cta-section p {
  color: #a1a1aa;
  margin-bottom: 2rem;
  font-size: 18px;
}

.btn-large {
  padding: 18px 48px;
  font-size: 18px;
}

/* Footer */
.footer {
  padding: 60px 2rem 30px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  margin-bottom: 3rem;
}

.footer-brand {
  max-width: 300px;
}

.footer-brand-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 1rem;
}

.footer-logo {
  height: 64px;
}


.footer-brand p {
  color: #71717a;
  font-size: 14px;
}

.footer-links {
  display: flex;
  gap: 4rem;
}

.footer-column h4 {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 1rem;
}

.footer-column a {
  display: block;
  color: #71717a;
  text-decoration: none;
  font-size: 14px;
  padding: 0.5rem 0;
  transition: color 0.2s;
}

.footer-column a:hover {
  color: #ffffff;
}

.footer-bottom {
  text-align: center;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.footer-bottom p {
  color: #71717a;
  font-size: 14px;
}

/* Responsive */
@media (max-width: 768px) {
  .navbar {
    padding: 0.75rem 1rem;
  }

  .nav-links {
    display: none;
  }

  .logo-img {
    height: 32px;
    max-width: 140px;
  }

  .nav-buttons {
    gap: 0.5rem;
  }

  .btn-login,
  .btn-signup {
    font-size: 13px;
    padding: 7px 12px;
  }

  /* Primary CTA in the nav on small screens */
  .btn-login {
    background: #ffffff;
    color: #0a0a0f;
    border-color: transparent;
  }

  .btn-login:hover {
    background: #e4e4e7;
    color: #0a0a0f;
  }

  .btn-signup {
    display: none;
  }
  
  h1 {
    font-size: 36px;
  }
  
  .features-grid, .pricing-cards {
    grid-template-columns: 1fr;
  }
  
  .steps {
    flex-direction: column;
  }
  
  .step-arrow {
    transform: rotate(90deg);
    margin: 0;
  }
  
  .footer-content {
    flex-direction: column;
    gap: 2rem;
  }
}
</style>
